import React, {useEffect, useState} from 'react';
import {Stack} from '@mui/material';
import {
  Modal,
  Button,
  Headline,
  Body,
  designSystemToken,
} from '@lightricks/react-design-system';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import extractBackendErrorDetail from '@/utils/extractBackendErrorDetail';
import raiseFlashMessage, {
  raiseFlashMessageError,
} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import Checkbox from '@/components/checkbox';
import FlashMessage from '@/components/flash-message';
import useCompleteContract from '@/hooks/mutations/use-complete-contract';

type CompleteContractModalProps = {
  open: boolean;
  onClose: () => void;
  contractId: string;
  creatorName?: string;
  acceptedAssetsCount?: number | null;
  requiredAssetsCount?: number | null;
  onSuccess?: () => void;
};

function CompleteContractModal({
  open,
  onClose,
  contractId,
  creatorName,
  acceptedAssetsCount,
  requiredAssetsCount,
  onSuccess,
}: CompleteContractModalProps) {
  const completeContract = useCompleteContract();
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (open) {
      AnalyticsService.dispatchEvent('open-modal', {event_type: 'Open Modal'});
    }
  }, [open]);

  const handleClose = () => {
    if (completeContract.isLoading) return;
    AnalyticsService.dispatchEvent('close-modal', {event_type: 'Cancel'});
    setAcknowledged(false);
    onClose();
  };

  const handleSubmit = () => {
    if (!acknowledged) return;

    AnalyticsService.dispatchEvent('move-to-complete', {
      event_type: 'Move to Complete',
    });

    completeContract.mutate(
      {contractId},
      {
        onSuccess: () => {
          raiseFlashMessage({
            status: 'success',
            mode: 'light',
            message: (
              <FlashMessage.Banner
                title={translate('Creator moved to complete')}
                subtitle={creatorName ?? ''}
              />
            ),
          });
          setAcknowledged(false);
          onSuccess?.();
          onClose();
        },
        onError: (error: unknown) => {
          raiseFlashMessageError({
            title: translate('Failed to move creator to complete'),
            subtitle: extractBackendErrorDetail(error),
          });
        },
      }
    );
  };

  const showAssetSummary =
    acceptedAssetsCount != null && requiredAssetsCount != null;

  return (
    <Modal open={open} handleClose={handleClose} size="small" showCloseButton>
      <Stack gap="16px" padding="24px" alignItems="flex-start">
        <Headline size="md">{translate('Move to complete')}</Headline>
        <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
          {translate(
            'This will end the contract, release payment, and notify the creator.'
          )}
        </Body>
        {showAssetSummary ? (
          <Body size="md" color={designSystemToken('semantic.fg.primary')}>
            {`${acceptedAssetsCount}/${requiredAssetsCount} ${translate('assets accepted')}`}
          </Body>
        ) : null}

        <Stack
          direction="row"
          alignItems="center"
          gap="8px"
          onClick={() => setAcknowledged((v) => !v)}
          sx={{cursor: 'pointer'}}
        >
          <Checkbox
            checked={acknowledged}
            onChange={() => setAcknowledged((v) => !v)}
          />
          <Body size="sm">
            {translate(
              'I understand this will finalize the contract and cannot be undone.'
            )}
          </Body>
        </Stack>

        <Stack
          direction="row"
          justifyContent="flex-end"
          gap="12px"
          sx={{width: '100%'}}
        >
          <Button
            mode="tinted"
            appearance="neutral"
            size="medium"
            onClick={handleClose}
            disabled={completeContract.isLoading}
          >
            {translate('Cancel')}
          </Button>
          <Button
            mode="filled"
            appearance="neutral"
            size="medium"
            onClick={handleSubmit}
            disabled={!acknowledged || completeContract.isLoading}
          >
            {translate('Complete')}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}

export default CompleteContractModal;
